import { useEffect, useState } from 'react';
import { useAuth } from './useAuth';
import { decodeJwt } from './jwt';

/** How many seconds before JWT expiry the warning fires. */
const WARN_BEFORE_SECONDS = 5 * 60;

/**
 * Schedules a warning to surface when the current JWT is about to expire.
 *
 * Returns `open` (whether the snackbar should be visible) and `dismiss` (to
 * close it without logging out). The warning resets whenever the token changes
 * (e.g. after re-login), so it fires again on the next token if that token
 * also has a short enough remaining lifetime.
 *
 * If the token has no `exp` claim or the warning window has already passed,
 * `open` will never be set to true by this hook.
 */
export function useSessionWarning() {
  const { token } = useAuth();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // No token means the user is logged out — nothing to warn about.
    if (!token) {
      setOpen(false);
      return;
    }

    const claims = decodeJwt(token);
    // Guard: if decode fails or exp is absent, degrade silently.
    if (!claims || typeof claims.exp !== 'number') {
      return;
    }

    const nowMs = Date.now();
    const expMs = claims.exp * 1000;
    const warnAtMs = expMs - WARN_BEFORE_SECONDS * 1000;
    const delayMs = warnAtMs - nowMs;

    // The warning window has already passed (e.g. page refreshed when token
    // had < 5 minutes left). Don't show a stale warning.
    if (delayMs <= 0) {
      return;
    }

    const timerId = window.setTimeout(() => {
      setOpen(true);
    }, delayMs);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [token]);

  const dismiss = () => setOpen(false);

  return { open, dismiss };
}
